import React, {useState} from "react";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {CallMade, GetApp} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import DateTime from "core/helpers/date/DateTime";
import { DATETIME_FORMAT } from "commons/constants/env";
import { ROUTES_ECRF } from "commons/constants/paths";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "commons/store/hooks";
import {
  selectTransferFileDownloading,
  transferDownloadFiles
} from "../../../../ecrf/redux/elementResourceTransferSlice";

// ==============================================================
type Props = { row: any; }
// ==============================================================

export default function RowDetails({ row }: Props) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());

  if (!row) return null;

  const handleDownload = async (rowId: any) => {
    setLoadingIds(prev => new Set(prev).add(rowId));
    try {
      await dispatch(transferDownloadFiles(rowId));
    } catch (e: any) {
      enqueueSnackbar(e?.detail || e?.message || t('Error'), { variant: 'error' });
    } finally {
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  };

  return (
    <Box sx={{ margin: 1 }}>
      <Typography variant="h6" gutterBottom component="div">
        {t('Elements')}
      </Typography>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Table size="medium" aria-label="drugs">
            <TableHead>
              <TableRow>
                <TableCell>{t('Label')}</TableCell>
                <TableCell>{t('Visit')}</TableCell>
                <TableCell>{t('Section')}</TableCell>
                <TableCell>{t('Transfers')}</TableCell>
                <TableCell>{t('Updated')}</TableCell>
                <TableCell align="right">{t('Action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row?.elements?.length > 0 ? row?.elements?.map((ed: any, index: any) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Button
                        component={Link}
                        disabled={!row?.ecrf?.id || !row?.sectionId}
                        to={{
                          pathname:(ROUTES_ECRF.DETAILS_EXTENDED(
                            row?.ecrf?.id || '', row?.sectionId || ''
                          )),
                          hash: '#' + row?.element?.id || ''
                        }}
                        variant="outlined"
                        color="primary"
                        size="small"
                        endIcon={<CallMade/>}
                      >
                        {ed?.label}
                      </Button>
                    </TableCell>
                    <TableCell>{ed?.visitName}</TableCell>
                    <TableCell>{ed?.sectionName}</TableCell>
                    <TableCell>{ed?.transfersCount}</TableCell>
                    <TableCell>{DateTime.toIso(ed?.updatedAt, DATETIME_FORMAT)}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => handleDownload(ed.id)}
                        loading={loadingIds.has(ed.id)}
                        style={{ width: "100%" }}
                        disabled={ed?.transfersCount === 0}
                      >
                        {loadingIds.has(ed.id)
                          ? <CircularProgress color={"inherit"} size={24} />
                          : <GetApp />
                        }
                      </Button>
                    </TableCell>
                  </TableRow>
                )) :
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('Empty data')}
                  </TableCell>
                </TableRow>
              }
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Box>
  );
}
